import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Clock } from "lucide-react";
import { api, IMAGES } from "@/lib/api";
import { Button } from "@/components/ui/button";

export const Services = ({ onBook }) => {
  const [services, setServices] = useState([]);

  useEffect(() => {
    api.get("/services").then(({ data }) => setServices(data)).catch(() => {});
  }, []);

  return (
    <section id="services" className="relative py-24 sm:py-32 bg-barber-bg radial-green overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-16">
          <div>
            <p className="uppercase tracking-[0.35em] text-barber-gold text-xs mb-4">What We Do</p>
            <h2 className="font-heading text-4xl sm:text-5xl font-bold text-barber-cream tracking-tight">
              Services & Pricing
            </h2>
          </div>
          <p className="text-barber-muted max-w-md leading-relaxed">
            From a quick clean-up to the full hot-towel treatment. Every service is done
            by hand, with care, and no rushing the chair.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s, i) => (
            <motion.div
              key={s.id}
              data-testid={`service-card-${s.id}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
              className="card-glow group relative bg-barber-surface border border-barber-gold/20 p-8 flex flex-col hover:border-barber-gold/60 hover:-translate-y-1 transition-all duration-300"
            >
              <span className="block h-0.5 w-10 bg-barber-gold mb-5 group-hover:w-16 transition-all duration-300" />
              <div className="flex items-start justify-between gap-4">
                <h3 className="font-heading text-2xl font-semibold text-barber-cream group-hover:text-barber-gold transition-colors">
                  {s.name}
                </h3>
                <span className="font-heading text-2xl text-barber-gold shrink-0">${s.price}</span>
              </div>
              <div className="flex items-center gap-2 text-barber-muted text-xs uppercase tracking-widest mt-2">
                <Clock className="w-3.5 h-3.5" /> {s.duration} min
              </div>
              <p className="text-barber-muted text-sm leading-relaxed mt-4 flex-1">
                {s.description}
              </p>
              <button
                data-testid={`service-book-${s.id}`}
                onClick={() => onBook(s.id)}
                className="mt-6 text-left text-sm uppercase tracking-widest text-barber-gold hover:text-barber-cream transition-colors"
              >
                Book this →
              </button>
            </motion.div>
          ))}
        </div>

        <div
          className="mt-16 relative overflow-hidden border border-barber-gold/20"
          style={{ backgroundImage: `url(${IMAGES.servicesBg})`, backgroundSize: "cover", backgroundPosition: "center" }}
        >
          <div className="bg-barber-bg/80 p-10 sm:p-14 flex flex-col sm:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="font-heading text-3xl text-barber-cream">Ready for a fresh look?</h3>
              <p className="text-barber-muted mt-2">Pick a time that works for you. It only takes a minute.</p>
            </div>
            <Button
              data-testid="services-cta-book"
              onClick={() => onBook()}
              className="rounded-none btn-gold font-semibold px-8 py-6 tracking-wide"
            >
              Book Appointment
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};
